/**
 * Keeping the board in step with the others: their drags shown while they last (dropped after
 * {@link STALE_MS} without news), and the view's "fit" and "centre on" moving the board's camera.
 */
import { createEffect, on } from "solid-js";
import type { BoxesDragged } from "../project";
import { STALE_CHECK_MS, STALE_MS } from "./constants";
import type { BoardCtx } from "./context";

/** Starts following the others' drags and the view's requests (for the page's life). */
export function followOthers(ctx: BoardCtx): void {
  const { view, project, presence } = ctx.deps;
  project.onBoxesDragged((dragged: BoxesDragged) => {
    if (dragged.by === presence.you() || dragged.view !== "board") return;
    const next = new Map(ctx.others());
    if (dragged.done) next.delete(dragged.by);
    else {
      const places = new Map(next.get(dragged.by)?.places);
      for (const [id, [x, y]] of Object.entries(dragged.positions)) {
        const sized = dragged.sizes?.[id];
        places.set(id, { x, y, ...(sized ? { w: sized[0], h: sized[1] } : {}) });
      }
      next.set(dragged.by, { places, at: Date.now() });
    }
    ctx.setOthers(next);
  });
  // On the board, "fit" and "centre on" move the board's view.
  createEffect(on(view.fitRequests, () => view.mode() === "board" && ctx.board.fit(), { defer: true }));
  createEffect(on(view.centerRequest, (request) => request && view.mode() === "board" && ctx.board.centerOn(request.id), { defer: true }));
  setInterval(() => {
    const now = Date.now();
    const stale = [...ctx.others()].filter(([, o]) => now - o.at > STALE_MS);
    if (!stale.length) return;
    const next = new Map(ctx.others());
    for (const [by] of stale) next.delete(by);
    ctx.setOthers(next);
  }, STALE_CHECK_MS);
}
